import { useState } from "react";
import { motion } from "framer-motion";
import { Check, FileDown, Mail, RotateCcw, X } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { exportSpecPdf } from "@/utils/specPdf";
import { CASSETTES } from "@/data/cassettes";
import { EN_CASSETTES } from "@/data/productsEn";
import { useLang } from "@/i18n";

const TONES = {
    qam: "bg-red-500/15 text-red-300 border-red-400/30",
    cofdm: "bg-cyan-500/15 text-cyan-300 border-cyan-400/30",
    t2c: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
    hqam: "bg-amber-500/15 text-amber-300 border-amber-400/30",
    hs: "bg-violet-500/15 text-violet-300 border-violet-400/30",
    dvbs: "bg-sky-500/15 text-sky-300 border-sky-400/30",
    frei: "bg-white/5 text-slate-500 border-white/10",
};

const LEGEND = [
    { tone: "qam", label: "SAT → QAM" },
    { tone: "cofdm", label: "SAT → COFDM" },
    { tone: "t2c", label: "DVB-T/T2/C → QAM" },
    { tone: "hqam", label: "HDMI → QAM" },
    { tone: "hs", label: "HDMI → DVB-S" },
    { tone: "dvbs", label: "DVB-S Loop" },
];

const MODELS = { "DH8-BASIS": 8, "DH4-BASIS": 4, "DH2-BASIS": 2 };
const DEFAULTS = { 8: [1, 1, 1, 1, 2, 3, 4, 0], 4: [1, 1, 2, 6], 2: [1, 6] };
const MODEL_PHOTOS = {
    "DH8-BASIS": { src: "/produkte/dh8-verdrahtet-front.webp", alt: "Müller DH8 Kopfstation geöffnet und vollständig verdrahtet — Frontansicht mit bestückten Kassetten" },
    "DH4-BASIS": { src: "/produkte/dh4-offen-frei.webp", alt: "Müller DH4 Kopfstation geöffnet — Blick auf die bestückten Kassetten" },
    "DH2-BASIS": { src: "/produkte/dh4-offen-frei.webp", alt: "Müller DH2 Kopfstation (Abbildung ähnlich DH4) — Blick auf die Kassetten" },
};

export default function HeadendConfigurator() {
    const [model, setModel] = useState("DH8-BASIS");
    const slots = MODELS[model];
    const [config, setConfig] = useState(DEFAULTS[8]);
    const [activeSlot, setActiveSlot] = useState(null);
    const { lang, t } = useLang();

    const pickModel = (m) => {
        setModel(m);
        setConfig(DEFAULTS[MODELS[m]]);
        setActiveSlot(null);
    };

    const assign = (idx, cassetteIdx) => {
        setConfig((cfg) => cfg.map((m, i) => (i === idx ? cassetteIdx : m)));
        setActiveSlot(null);
    };

    const mailHref = `mailto:vertrieb@satmueller.de?subject=${encodeURIComponent(
        lang === "en"
            ? `Enquiry: ${model} headend (configured)`
            : `Anfrage: ${model} Kopfstation (konfiguriert)`
    )}&body=${encodeURIComponent(
        lang === "en"
            ? `Hello,\n\nI am interested in a ${model} headend with the following configuration:\n\n${config
                .map((m, i) => {
                    const c = CASSETTES[m];
                    return c.sku === "—"
                        ? `Slot ${i + 1}: empty`
                        : `Slot ${i + 1}: ${c.sku} ${c.code} — ${c.desc}`;
                })
                .join("\n")}\n\nPlease get back to me.\n\nBest regards`
            : `Guten Tag,\n\nich interessiere mich für eine ${model}-Kopfstation mit folgender Bestückung:\n\n${config
                .map((m, i) => {
                    const c = CASSETTES[m];
                    return c.sku === "—"
                        ? `Slot ${i + 1}: frei`
                        : `Slot ${i + 1}: ${c.sku} ${c.code} — ${c.desc}`;
                })
                .join("\n")}\n\nBitte melden Sie sich bei mir.\n\nMit freundlichen Grüßen`
    )}`;

    const gridCols = slots === 8 ? "grid-cols-4 sm:grid-cols-8" : slots === 4 ? "grid-cols-4" : "grid-cols-2";

    return (
        <section id="kopfstationen" data-testid="kopfstationen" className="scroll-mt-24 py-16 sm:py-24">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
                <div className="relative overflow-hidden rounded-3xl bg-slate-950 text-white border border-slate-800 shadow-xl">
                    <div className="absolute inset-0 bg-blueprint-dark" aria-hidden="true" />
                    <div className="absolute -top-32 -right-24 h-96 w-96 rounded-full bg-red-600/20 blur-3xl" aria-hidden="true" />
                    <div className="relative grid lg:grid-cols-12 gap-10 p-6 sm:p-10 lg:p-14">
                        <div className="lg:col-span-5">
                            <Reveal>
                                <Eyebrow light>{t("Eigene Fertigung", "In-house manufacturing")}</Eyebrow>
                                <h2 className="mt-5 font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">
                                    {t("Kopfstationen nach Maß: die DHx-Serie.", "Custom headends: the DHx series.")}
                                </h2>
                                <p className="mt-4 text-slate-400 leading-relaxed text-sm sm:text-base">
                                    {t(
                                        "Wir fertigen Kabelkopfstationen genau nach Ihren Angaben. Testen Sie hier die Bestückung: Wählen Sie das Basisgerät, klicken Sie einen Kassettenplatz an und wählen die passende Kassette.",
                                        "We build cable headends exactly to your specification. Try the configuration here: pick the base unit, click a cassette slot and choose the matching cassette."
                                    )}
                                </p>
                                <div className="mt-7 flex flex-wrap gap-2">
                                    {LEGEND.map((l) => (
                                        <span key={l.tone} className={`inline-flex items-center gap-1.5 rounded border px-2 py-1 font-mono text-[10px] ${TONES[l.tone]}`}>
                                            <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
                                            {l.label}
                                        </span>
                                    ))}
                                </div>
                                <motion.img
                                    key={model}
                                    src={MODEL_PHOTOS[model].src}
                                    alt={MODEL_PHOTOS[model].alt}
                                    loading="lazy"
                                    data-testid="konfigurator-produktfoto"
                                    initial={{ opacity: 0, scale: 0.97 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                                    className="mt-8 w-full max-w-md drop-shadow-[0_25px_45px_rgba(0,0,0,0.55)]"
                                />
                                <div
                                    className="mt-5 inline-flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-4 py-3"
                                    data-testid="konfigurator-garantie"
                                >
                                    <img
                                        src="/produkte/garantie-4jahre.webp"
                                        alt="4 Jahre Garantie"
                                        loading="lazy"
                                        className="h-14 w-auto"
                                    />
                                    <p className="text-sm font-semibold text-slate-200 leading-snug">
                                        {t(
                                            <span key="de">4 Jahre Garantie auf unsere<br />Kopfstationen &amp; Steckkarten</span>,
                                            <span key="en">4-year warranty on our<br />headends &amp; plug-in cards</span>
                                        )}
                                    </p>
                                </div>
                            </Reveal>
                        </div>

                        <div className="lg:col-span-7" data-testid="headend-configurator">
                            <Reveal delay={0.1}>
                                <div className="rounded-2xl border border-white/10 bg-slate-900/70 backdrop-blur p-5 sm:p-7">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <div className="inline-flex rounded-lg border border-white/10 bg-slate-950/60 p-1" role="group" aria-label={t("Basisgerät wählen", "Choose base unit")}>
                                            {Object.keys(MODELS).map((m) => (
                                                <button
                                                    key={m}
                                                    onClick={() => pickModel(m)}
                                                    data-testid={`config-model-${m.split("-")[0].toLowerCase()}`}
                                                    className={`rounded-md px-4 py-2 font-mono text-xs tracking-wider transition-colors ${
                                                        model === m
                                                            ? "bg-red-600 text-white shadow"
                                                            : "text-slate-400 hover:text-white"
                                                    }`}
                                                >
                                                    {m}
                                                </button>
                                            ))}
                                        </div>
                                        <button
                                            onClick={() => {
                                                setConfig(DEFAULTS[slots]);
                                                setActiveSlot(null);
                                            }}
                                            data-testid="config-reset"
                                            className="inline-flex items-center gap-1.5 rounded-md border border-white/10 px-3 py-2 font-mono text-[11px] text-slate-400 hover:text-white hover:border-white/30 transition-colors"
                                        >
                                            <RotateCcw className="h-3.5 w-3.5" />
                                            Reset
                                        </button>
                                    </div>

                                    <div className={`mt-6 grid ${gridCols} gap-2.5`}>
                                        {config.map((m, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setActiveSlot(activeSlot === i ? null : i)}
                                                data-testid={`config-slot-${i}`}
                                                title={t("Klicken zum Bestücken", "Click to assign")}
                                                className={`group relative h-20 sm:h-24 rounded-lg border text-left px-2.5 py-2 transition-all duration-200 hover:scale-[1.03] ${TONES[CASSETTES[m].tone]} ${
                                                    activeSlot === i ? "ring-2 ring-white/70" : ""
                                                }`}
                                            >
                                                <span className="block font-mono text-[9px] uppercase tracking-widest opacity-60">
                                                    Slot {i + 1}
                                                </span>
                                                <span className="block font-mono text-[8.5px] sm:text-[9px] opacity-70">{CASSETTES[m].sku}</span>
                                                <span className="block font-mono text-[10px] sm:text-[11px] font-semibold leading-tight">
                                                    {CASSETTES[m].code}
                                                </span>
                                                <span className="absolute bottom-2 right-2 h-1.5 w-1.5 rounded-full bg-current opacity-40 group-hover:opacity-100 transition-opacity" />
                                            </button>
                                        ))}
                                    </div>

                                    {activeSlot !== null && (
                                        <div className="mt-4 rounded-xl border border-white/15 bg-slate-950/80 p-4" data-testid="config-picker">
                                            <div className="flex items-center justify-between gap-3">
                                                <p className="font-mono text-[11px] uppercase tracking-widest text-slate-300">
                                                    {t(`Slot ${activeSlot + 1} bestücken — Kassette wählen`, `Assign slot ${activeSlot + 1} — choose a cassette`)}
                                                </p>
                                                <button
                                                    onClick={() => setActiveSlot(null)}
                                                    data-testid="config-picker-close"
                                                    aria-label={t("Auswahl schließen", "Close selection")}
                                                    className="inline-flex items-center justify-center h-7 w-7 rounded-md border border-white/10 text-slate-400 hover:text-white hover:border-white/30 transition-colors"
                                                >
                                                    <X className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                            <div className="mt-3 max-h-64 overflow-y-auto pr-1 space-y-1.5">
                                                {CASSETTES.map((c, ci) => (
                                                    <button
                                                        key={`${c.sku}-${ci}`}
                                                        onClick={() => assign(activeSlot, ci)}
                                                        data-testid={`cassette-option-${ci}`}
                                                        className={`w-full flex items-start gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors ${TONES[c.tone]} ${
                                                            config[activeSlot] === ci ? "ring-1 ring-white/60" : "hover:bg-white/5"
                                                        }`}
                                                    >
                                                        <span className="font-mono text-[10px] pt-0.5 opacity-80 shrink-0">{c.sku}</span>
                                                        <span className="min-w-0">
                                                            <span className="block font-mono text-[11px] font-semibold">{c.code}</span>
                                                            <span className="block text-[11px] leading-snug opacity-80">{lang === "en" ? EN_CASSETTES[c.code] ?? c.desc : c.desc}</span>
                                                        </span>
                                                        {config[activeSlot] === ci && <Check className="h-4 w-4 ml-auto shrink-0" />}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    <div className="mt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-t border-white/10 pt-5">
                                        <div className="font-mono text-[11px] text-slate-400 leading-relaxed" data-testid="config-readout">
                                            <span className="text-slate-200">{model}</span> · {t(`${slots} Kassettenplätze`, `${slots} cassette slots`)}
                                            <br />
                                            {t("Bestückung:", "Configuration:")} {config.map((m) => CASSETTES[m].code).join(" · ")}
                                        </div>
                                        <div className="flex shrink-0 flex-wrap gap-2">
                                            <button
                                                onClick={() =>
                                                    exportSpecPdf({
                                                        model,
                                                        slots,
                                                        config,
                                                        casettes: CASSETTES.map((c) => ({
                                                            ...c,
                                                            desc: lang === "en" ? EN_CASSETTES[c.code] ?? c.desc : c.desc,
                                                        })),
                                                        lang,
                                                    })
                                                }
                                                data-testid="config-pdf-export"
                                                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 hover:border-white/60 text-white font-semibold text-sm px-5 py-3 transition-colors"
                                            >
                                                <FileDown className="h-4 w-4" />
                                                {t("Spec-Blatt (PDF)", "Spec sheet (PDF)")}
                                            </button>
                                            <a
                                                href={mailHref}
                                                data-testid="config-mailto"
                                                className="inline-flex items-center justify-center gap-2 rounded-full bg-white text-slate-900 hover:bg-red-50 font-semibold text-sm px-6 py-3 transition-colors"
                                            >
                                                <Mail className="h-4 w-4" />
                                                {t("Konfiguration anfragen", "Request this configuration")}
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
